import {
  AbsoluteFill,
  interpolate,
  spring,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import { BRAND, FONTS } from "./theme";

/**
 * Crisp heading overlay with a 3D perspective tilt + neon glow + spring
 * entrance. Rendered as a DOM sibling of <Stage> (NOT a 3D mesh) — drei/troika
 * <Text> does not load its font in Remotion's headless render, so titles use
 * reliable HTML-with-perspective. The scene's depth comes from the 3D content
 * behind it (hero meshes, camera drift); this keeps title text razor-sharp.
 */
export const Title: React.FC<{
  text: string;
  subtitle?: string;
  accent?: string;
  /** Vertical anchor of the title block. */
  place?: "center" | "top" | "upper";
  delay?: number;
  size?: number;
}> = ({
  text,
  subtitle,
  accent = BRAND.cyan,
  place = "center",
  delay = 0,
  size = 96,
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const s = spring({
    frame: frame - delay,
    fps,
    config: { damping: 200, mass: 0.7 },
  });
  const ty = interpolate(s, [0, 1], [40, 0]);
  const justify =
    place === "top"
      ? "flex-start"
      : place === "upper"
        ? "flex-start"
        : "center";
  const paddingTop = place === "top" ? 120 : place === "upper" ? 240 : 0;

  return (
    <AbsoluteFill
      style={{
        justifyContent: justify,
        alignItems: "center",
        paddingTop,
        pointerEvents: "none",
      }}
    >
      <div
        style={{
          transform: `perspective(1400px) rotateX(8deg) translateY(${ty}px)`,
          opacity: s,
          textAlign: "center",
          maxWidth: 1500,
        }}
      >
        <div
          style={{
            fontFamily: FONTS.heading,
            fontWeight: 800,
            fontSize: size,
            lineHeight: 1.05,
            color: BRAND.white,
            letterSpacing: -1,
            textShadow: `0 0 32px ${accent}, 0 6px 40px rgba(0,0,0,0.6)`,
          }}
        >
          {text}
        </div>
        {subtitle && (
          <div
            style={{
              marginTop: 22,
              fontFamily: FONTS.body,
              fontSize: 40,
              color: BRAND.textDim,
              textShadow: `0 2px 20px ${BRAND.space0}`,
            }}
          >
            {subtitle}
          </div>
        )}
        <div
          style={{
            margin: "26px auto 0",
            width: 220,
            height: 5,
            borderRadius: 4,
            background: `linear-gradient(90deg, transparent, ${accent}, transparent)`,
            transform: `scaleX(${s})`,
          }}
        />
      </div>
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
